import React, { useState } from 'react';
import { CheckCircle, ChevronDown, ExternalLink, Image as ImageIcon, XCircle } from 'lucide-react';
import Modal from './Modal';

const ROW = 'px-4 py-2.5';
const LABEL = 'text-sm font-medium text-blue-gray-800';
const DETAIL = 'text-xs text-blue-gray-600 leading-relaxed';
const STATUS_PILL = 'text-[10px] font-medium px-1.5 py-0.5 rounded-md border';

function ImagePreviewThumb({ src, onPreview }) {
  const [error, setError] = useState(false);
  const showImg = src && !error && !src.startsWith('data:');

  if (!showImg) {
    return (
      <div className="w-9 h-9 shrink-0 rounded-md border border-neutral-200 bg-neutral-50 overflow-hidden flex items-center justify-center">
        <ImageIcon size={14} className="text-blue-gray-300" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onPreview(src)}
      className="w-9 h-9 shrink-0 rounded-md border border-neutral-200 bg-neutral-50 overflow-hidden flex items-center justify-center cursor-zoom-in hover:ring-2 hover:ring-accent/30 hover:border-accent/40 transition-shadow focus:outline-none focus:ring-2 focus:ring-accent/40"
      title="Afficher l'image en grand"
    >
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover pointer-events-none"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setError(true)}
      />
    </button>
  );
}

export default function ImagesAltPanel({
  images = [],
  nbImages = 0,
  nbWithoutAlt = 0,
  imagesOk = true,
}) {
  const [open, setOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const withAlt = nbImages > 0 ? nbImages - nbWithoutAlt : 0;

  const closePreview = () => {
    setPreviewSrc(null);
    setPreviewFailed(false);
  };

  const openPreview = (src) => {
    setPreviewFailed(false);
    setPreviewSrc(src);
  };

  if (!nbImages) {
    return (
      <p className={`${DETAIL} italic ${ROW}`}>
        Aucune image détectée sur cette page.
      </p>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 ${ROW} text-left hover:bg-neutral-50/80 transition-colors`}
        aria-expanded={open}
      >
        {imagesOk ? (
          <CheckCircle size={14} className="text-green-500 shrink-0" />
        ) : (
          <XCircle size={14} className="text-red-400 shrink-0" />
        )}
        <span className={`${LABEL} flex-1`}>Images avec alt</span>
        <span
          className={`text-xs font-semibold tabular-nums shrink-0 ${
            imagesOk ? 'text-green-600' : 'text-red-500'
          }`}
        >
          {withAlt}/{nbImages}
        </span>
        <ChevronDown
          size={14}
          className={`text-blue-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-3 pt-0 border-t border-neutral-50">
          {!imagesOk && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 mt-2 mb-2 leading-relaxed">
              {nbWithoutAlt} image{nbWithoutAlt > 1 ? 's' : ''} sans attribut alt — Google ne peut pas les interpréter.
            </p>
          )}
          {images.length === 0 ? (
            <p className={`${DETAIL} italic mt-2`}>
              Détail non disponible pour cet audit. Relancez l&apos;audit SEO de la page pour afficher la liste complète.
            </p>
          ) : (
            <ul className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 max-h-56 overflow-y-auto mt-2">
              {images.map((img, i) => (
                <li
                  key={`${img.src || 'img'}-${i}`}
                  className="flex items-center gap-2.5 px-2.5 py-2 hover:bg-neutral-50/80"
                >
                  <ImagePreviewThumb src={img.src} onPreview={openPreview} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {img.has_alt ? (
                        <span className={`${STATUS_PILL} text-green-700 bg-green-50 border-green-200`}>
                          Alt OK
                        </span>
                      ) : (
                        <span className={`${STATUS_PILL} text-red-700 bg-red-50 border-red-200`}>
                          Sans alt
                        </span>
                      )}
                      {img.alt && (
                        <span className={`${DETAIL} text-blue-gray-800`} title={img.alt}>
                          « {img.alt.slice(0, 55)}{img.alt.length > 55 ? '…' : ''} »
                        </span>
                      )}
                    </div>
                    {img.src && (
                      <p className="text-[11px] text-blue-gray-400 font-mono truncate mt-1" title={img.src}>
                        {img.src.replace(/^https?:\/\//, '')}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {images.length > 0 && images.length < nbImages && (
            <p className="text-[11px] text-blue-gray-400 mt-2">
              {images.length} sur {nbImages} — relancez l&apos;audit pour la liste complète.
            </p>
          )}
        </div>
      )}

      <Modal open={!!previewSrc} onClose={closePreview} title="Aperçu de l'image" size="xl">
        {previewSrc && (
          <div className="flex flex-col items-center gap-3">
            {!previewFailed ? (
              <img
                src={previewSrc}
                alt="Aperçu en taille réelle"
                className="max-w-full max-h-[min(70vh,640px)] w-auto object-contain rounded-lg border border-neutral-200 bg-neutral-50"
                referrerPolicy="no-referrer"
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <p className={`${DETAIL} text-center py-6`}>
                L&apos;aperçu est bloqué par le serveur d&apos;images. Ouvrez le lien ci-dessous.
              </p>
            )}
            <a
              href={previewSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline break-all text-center"
            >
              Ouvrir dans un nouvel onglet
              <ExternalLink size={14} className="shrink-0" />
            </a>
            <p className="text-[11px] text-blue-gray-400 font-mono break-all text-center max-w-full">
              {previewSrc.replace(/^https?:\/\//, '')}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
